import {
  Box,
  ControlPrefix,
  DateRangePicker,
  DEFAULT_CHART_SHORTCUT_OPTIONS,
  FormControlSizes,
  HStack,
  type DateRangeOrLookback,
} from '@talos/kyoko';
import { AggregatedAssetSelect } from '../AggregatedMetric/AggregatedAssetSelect';
import { AggregatedMetricSelect } from '../AggregatedMetric/AggregatedMetricSelect';

const ASSET_CONTROL_WIDTH = '170px';
const METRIC_CONTROL_WIDTH = '150px';
const DATE_RANGE_CONTROL_WIDTH = '150px';

interface ComparisonToolbarProps {
  metric: string;
  availableMetrics: string[];
  isMetricsLoading: boolean;
  isMetricsError: boolean;
  onMetricChange: (metric: string) => void;
  /** Maps a metric ID to its display label. */
  getMetricLabel: (metricId: string) => string;
  selectedTalosSymbols: string[];
  availableTalosSymbols: string[];
  onSelectedTalosSymbolsChange: (symbols: string[]) => void;
  maxSelectedAssets: number;
  /** Current date range or active lookback shortcut — passed directly to the picker so shortcuts stay highlighted. */
  dateRange: DateRangeOrLookback;
  onDateRangeChange: (value: DateRangeOrLookback) => void;
}

export function ComparisonToolbar({
  metric,
  availableMetrics,
  isMetricsLoading,
  isMetricsError,
  onMetricChange,
  getMetricLabel,
  selectedTalosSymbols,
  availableTalosSymbols,
  onSelectedTalosSymbolsChange,
  maxSelectedAssets,
  dateRange,
  onDateRangeChange,
}: ComparisonToolbarProps) {
  return (
    <HStack gap="spacing8">
      <AggregatedAssetSelect
        availableTalosSymbols={availableTalosSymbols}
        selectedTalosSymbols={selectedTalosSymbols}
        onSelectedTalosSymbolsChange={onSelectedTalosSymbolsChange}
        maxSelections={maxSelectedAssets}
        width={ASSET_CONTROL_WIDTH}
        buttonTestID="comparison-toolbar-assets"
        dropdownTestID="comparison-toolbar-assets-dropdown"
      />
      <AggregatedMetricSelect
        metric={metric}
        availableMetrics={availableMetrics}
        isLoading={isMetricsLoading}
        isError={isMetricsError}
        hasSelectedAssets={selectedTalosSymbols.length > 0}
        onMetricChange={onMetricChange}
        getLabel={getMetricLabel}
        prefix={<ControlPrefix>Metric</ControlPrefix>}
        width={METRIC_CONTROL_WIDTH}
        testID="comparison-toolbar-metric"
      />
      <Box w={DATE_RANGE_CONTROL_WIDTH}>
        <DateRangePicker
          value={dateRange}
          onChange={onDateRangeChange}
          shortcuts={DEFAULT_CHART_SHORTCUT_OPTIONS}
          size={FormControlSizes.Small}
          clearable={false}
          maxValue={new Date()}
          data-testid="comparison-toolbar-date-range"
        />
      </Box>
    </HStack>
  );
}
